import React, { FC, useRef, useState } from 'react';
import style from './fileInput.module.css';
import AppButton from '../../Buttons/AppButton';
import NewProgressBar from '../../Loaders/NewProgressBar';

interface FileInputProps {
  label?: string;
  error?: string;
  touched?: boolean;
  name?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  multiple?: boolean;
  logoUrl?: string;
  onDeleteLogo?: () => void;
  isDeletable?: boolean; 
  loading?: boolean;
  progress?: number;
  formats: string[];
  placeholder?: string;
  circlePreview?: boolean;
}

const FileInput: FC<FileInputProps> = ({
  label = "",
  error,
  touched,
  name = "fileInput",
  onChange,
  onBlur,
  multiple = false,
  logoUrl,
  onDeleteLogo,
  loading = false,
  progress,
  formats,
  isDeletable = false,
  placeholder = "Arrastra y suelta el archivo aquí o haz clic para seleccionarlo",
  circlePreview = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const acceptAttr = formats
    .map(ext => ext.startsWith('.') ? ext : `.${ext}`)
    .join(',');

  const handleUpdateClick = () => {
    if (!loading) fileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (loading) return;
    if (onChange && e.dataTransfer.files.length > 0) {
      const event = {
        target: { files: e.dataTransfer.files },
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      onChange(event);
      e.dataTransfer.clearData();
    }
  };

  return (
    <div className={style.container__input}>
      {label && (
        <label className={style.input__label} htmlFor={name}>
          {label}
        </label>
      )}

      {logoUrl ? (
        <div className={`${style.logoContainer} ${circlePreview ? style.circlePreview : ""}`}>
          <img src={logoUrl} alt="Logo" className={`${style.logoImage} ${circlePreview ? style.circleImage : ""}`} />

          {loading && (
            <div className={style.overlay}>
              <NewProgressBar
                className={style.contentLoadig}
                size="large"
                text={progress !== undefined ? `Cargando... ${progress}%` : "Cargando..."}
                variant="indeterminate"
              />
            </div>
          )}

          {!loading && (
            <div className={style.buttonsContainer}>
              {isDeletable && (
                <AppButton
                  iconName="trash"
                  type="button"
                  onClick={e => { e.stopPropagation(); onDeleteLogo && onDeleteLogo(); }}
                  isIconOnly
                  variant="secondary"
                  size='small'
                />
              )}
              <AppButton
                iconName="edit"
                type="button"
                isIconOnly
                onClick={handleUpdateClick}
                variant="secondary"
                size='small'
              />
            </div>
          )}
        </div>
      ) : (
        <div
          className={`${style.fileInputContainer} ${isDragOver ? style.dragOver : ""}`}
          onClick={e => { e.preventDefault(); handleUpdateClick(); }}
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {loading ? (
            <div className={style.loadingContainer}>
              <NewProgressBar
                className={style.contentLoadig}
                size="large"
                text={progress !== undefined ? `Cargando... ${progress}%` : "Cargando..."}
                variant="indeterminate"
              />
            </div>
          ) : (
            <>
              <p className={style.fileInputFormats}>
                {isDragOver
                  ? "Suelta el archivo aquí para subirlo"
                  : placeholder
                }
              </p>
              {!isDragOver && (
                <div className={style.uploadButton}>
                  <AppButton
                    buttonText='Elige un archivo'
                    type="button"
                    variant="filled" 
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        id={name}
        type="file"
        name={name}
        accept={acceptAttr}
        onChange={onChange}
        onBlur={onBlur}
        multiple={multiple}
        className={style.hiddenInput}
        disabled={loading}
      />

      {!logoUrl && !isDragOver && (
        <small className={style.formatsInfo}>
          Formatos permitidos: {formats.map(f => f.replace(/^\./, '')).join(', ')}
        </small>
      )}

      {touched && error && <div className={style.error}>{error}</div>}
    </div>
  );
};

export default FileInput;
